// Payment service for interacting with Stripe via backend API

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

interface CreateCheckoutSessionParams {
  userId: string;
  tier: 'pro' | 'power';
  period: 'monthly' | 'yearly';
  /** If true and tier is pro, apply early-adopter coupon when slots remain (first 200 → $4.99/mo) */
  earlyAdopter?: boolean;
}

interface CreateCheckoutSessionResponse {
  checkoutUrl: string;
  sessionId: string;
}

interface CustomerPortalResponse {
  url: string;
}

interface VerifySessionResponse {
  success: boolean;
  tier?: string;
  subscriptionId?: string;
  customerId?: string;
  currentPeriodEnd?: string;
  error?: string;
}

type AuthTokens = { bearerToken: string | null; accessToken: string | null };

async function getAuthTokens(): Promise<AuthTokens> {
  try {
    const { storageService } = await import('./storageService');
    const session = await storageService.getAuthSession();
    return {
      bearerToken: session?.idToken || session?.accessToken || null,
      accessToken: session?.accessToken || null,
    };
  } catch (error) {
    console.warn('Failed to get auth token:', error);
    return { bearerToken: null, accessToken: null };
  }
}

export const paymentService = {
  /**
   * Create checkout session and get Stripe Checkout URL
   */
  async createCheckoutSession(
    params: CreateCheckoutSessionParams
  ): Promise<CreateCheckoutSessionResponse> {
    const tokens = await getAuthTokens();
    
    const response = await fetch(`${API_BASE_URL}/subscription/create-checkout`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(tokens.bearerToken && { Authorization: `Bearer ${tokens.bearerToken}` }),
        ...(tokens.accessToken && { 'x-access-token': tokens.accessToken }),
      },
      body: JSON.stringify({
        userId: params.userId,
        tier: params.tier,
        period: params.period,
        ...(params.earlyAdopter !== undefined && { earlyAdopter: params.earlyAdopter }),
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to create checkout session' }));
      console.error('Checkout session API failed:', { status: response.status, error });
      if (response.status === 401) {
        throw new Error('Please sign in again to upgrade.');
      }
      throw new Error(error.error || error.message || 'Unable to start checkout. Please try again.');
    }

    return response.json();
  },

  /**
   * Get customer portal URL for subscription management
   */
  async getCustomerPortalUrl(userId: string): Promise<string> {
    const tokens = await getAuthTokens();
    
    const response = await fetch(`${API_BASE_URL}/subscription/customer-portal`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(tokens.bearerToken && { Authorization: `Bearer ${tokens.bearerToken}` }),
        ...(tokens.accessToken && { 'x-access-token': tokens.accessToken }),
      },
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to get customer portal URL' }));
      console.error('Customer portal API failed:', { status: response.status, error });
      throw new Error(error.error || error.message || 'Unable to open billing portal. Please try again.');
    }

    const data: CustomerPortalResponse = await response.json();
    return data.url;
  },

  /**
   * Verify checkout session after redirect
   */
  async verifyCheckoutSession(sessionId: string): Promise<VerifySessionResponse> {
    const tokens = await getAuthTokens();
    
    const response = await fetch(`${API_BASE_URL}/subscription/verify-session?session_id=${sessionId}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        ...(tokens.bearerToken && { Authorization: `Bearer ${tokens.bearerToken}` }),
        ...(tokens.accessToken && { 'x-access-token': tokens.accessToken }),
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to verify session' }));
      console.error('Verify checkout API failed:', { status: response.status, error });
      return {
        success: false,
        error: error.error || error.message || 'Unable to verify checkout status.',
      };
    }

    return response.json();
  },
};
